import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PatientCard } from '@/components/patients/PatientCard';
import { SearchIcon } from '@/components/ui/Icons';
import { mockPatients, mockExamsByPatient } from '@/data/mock';
import type { Status } from '@/lib/types';

type Filter = 'all' | Status;

export function Patients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');

  // Filter from URL (?status=crit) — keeps it shareable / refreshable
  const filterParam = (searchParams.get('status') ?? 'all') as Filter;
  const filter: Filter = ['all', 'ok', 'warn', 'crit'].includes(filterParam)
    ? filterParam
    : 'all';

  const setFilter = (next: Filter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') {
      params.delete('status');
    } else {
      params.set('status', next);
    }
    setSearchParams(params, { replace: true });
  };

  // Sync query → URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (query.trim()) {
      params.set('q', query.trim());
    } else {
      params.delete('q');
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const counts = useMemo(
    () => ({
      all: mockPatients.length,
      ok: mockPatients.filter((p) => p.status === 'ok').length,
      warn: mockPatients.filter((p) => p.status === 'warn').length,
      crit: mockPatients.filter((p) => p.status === 'crit').length,
    }),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mockPatients
      .filter((p) => filter === 'all' || p.status === filter)
      .filter((p) => {
        if (!q) return true;
        const haystack = [
          p.name,
          p.email,
          p.optId,
          p.city,
          p.country,
          p.age.toString(),
          p.gender,
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
  }, [filter, query]);

  return (
    <main>
      <div className="pane-content appear">
        <header className="mb-6">
          <h1 className="pane-title">Pacientes</h1>
          <p className="pane-summary">
            {counts.all} activos · busca o filtra por status. Click en un paciente para ver su perfil.
          </p>
        </header>

        <div className="flex items-center gap-2.5 mb-3.5 flex-wrap">
          <div className="flex-1 min-w-[240px] bg-glass backdrop-blur-md border border-line-glass rounded-md shadow-recessed px-3 flex items-center gap-2.5 h-[38px] transition-all focus-within:bg-surface focus-within:border-line-default">
            <SearchIcon />
            <input
              type="search"
              placeholder="Buscar por nombre, email o ID…"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 min-w-0 border-0 outline-0 bg-transparent text-sm placeholder:text-faint"
            />
          </div>
        </div>

        <div className="flex gap-1.5 mb-3.5 flex-wrap">
          <FilterChip
            active={filter === 'all'}
            count={counts.all}
            label="Todos"
            onClick={() => setFilter('all')}
          />
          <FilterChip
            active={filter === 'ok'}
            count={counts.ok}
            label="Óptimo"
            onClick={() => setFilter('ok')}
          />
          <FilterChip
            active={filter === 'warn'}
            count={counts.warn}
            label="Mejorable"
            onClick={() => setFilter('warn')}
          />
          <FilterChip
            active={filter === 'crit'}
            count={counts.crit}
            label="Prioritario"
            onClick={() => setFilter('crit')}
          />
        </div>

        <div className="glass-panel appear-stagger">
          {filtered.map((p) => (
            <PatientCard
              key={p.id}
              patient={p}
              exams={mockExamsByPatient[p.id] ?? []}
            />
          ))}
          {filtered.length === 0 && (
            <div className="py-6 text-center text-muted text-[13px]">
              No hay pacientes que coincidan.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

interface FilterChipProps {
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
}

function FilterChip({ active, count, label, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium select-none transition-all border ${
        active
          ? 'bg-primary text-surface border-primary shadow-pressed'
          : 'bg-soft text-secondary border-transparent shadow-pill hover:bg-subtle hover:border-line-default'
      }`}
    >
      {label}{' '}
      <span
        className={`text-[11px] tnum font-semibold ${
          active ? 'text-surface/60' : 'text-muted'
        }`}
      >
        {count}
      </span>
    </button>
  );
}
